import type { PropsWithChildren } from "react";
import { useState } from "react";

import { useUser } from "@ui/hooks/useUser";
import { useTranslations } from "@ui/providers/useTranslations";
import { replaceStringPlaceholder } from "@ui/utils/replaceStringPlaceholder/replaceStringPlaceholder";
import classNames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import type { FilePickerProps } from "@jti/core-components/components/FilePicker";
import type { Steps } from "@jti/fe-common";
import { useContestsBackendConfig } from "@jti/rest-api";
import type { GetContestsResponseType } from "@jti/rest-api-types";

import type { ContactFormProps } from "../ContactForm";
import { ConsentCheckbox, ContactForm } from "../ContactForm";
import { DEPRECATED_Loading } from "../DEPRECATED_Loading";
import { Typography } from "../Typography";

import { LoyaltyContestFormProvider } from "./components";

import styles from "./LoyaltyContactForm.module.scss";

const getFilePickerPropsFromConfig = (config: GetContestsResponseType): FilePickerProps => {
  return {
    acceptedFileTypes: config.file_mime_types,
    maxFiles: config.files_limit,
    maxSize: config.file_size_limit,
  };
};

export type LoyaltyContactFormProps = {
  heading: string;
  subHeading: string;
  imageSrc: string;
  isInEditor: boolean;
  successHeading: string;
  successSubHeading: string;
  successFormHeading: string;
  successFormSubHeading: string;
  theme?: "light" | "dark";
  consent: Steps;
  showRequiredAsterisk?: boolean;
  contestId?: string;
} & ContactFormProps;

export const LoyaltyContactForm = ({
  heading,
  subHeading,
  imageSrc,
  isInEditor,
  successHeading,
  successSubHeading,
  successFormHeading,
  successFormSubHeading,
  theme = "light",
  consent = "contact_form",
  children,
  showRequiredAsterisk = false,
  contestId,
  ...contactFormProps
}: PropsWithChildren<LoyaltyContactFormProps>) => {
  const { __ } = useTranslations();
  const { isUserLoggedIn, user } = useUser();
  const { isLoyaltyContestsServiceEnabled } = useConfig();
  const { isContestsBackendConfigError, isContestsBackendConfigLoading, contestsBackedConfig } =
    useContestsBackendConfig({
      ciam_token: user?.ciam_token ?? null,
      uuid: user?.uuid ?? null,
      isInEditor,
    });
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const hasReachedParticipationLimit =
    contestsBackedConfig &&
    contestsBackedConfig.month_participation_count >= contestsBackedConfig.month_participation_limit &&
    !isInEditor &&
    contestsBackedConfig.month_participation_limit !== 0;

  if (!isUserLoggedIn && !isInEditor) return null;

  if (isContestsBackendConfigLoading) return <DEPRECATED_Loading />;

  if (isContestsBackendConfigError) {
    console.error("Error fetching contests backend config:", isContestsBackendConfigError);
    return <AlertBanner tone="error" title={__("loyalty.contact_form.error.title")} />;
  }

  const [title, description, formHeading, formSubHeading] = isSuccess
    ? [successHeading, successSubHeading, successFormHeading, successFormSubHeading]
    : [contactFormProps.title, contactFormProps.description, heading, subHeading];

  const hiddenFields = [
    { key: "First name", value: user?.first_name ?? "" },
    { key: "Last name", value: user?.last_name ?? "" },
    { key: "Email", value: user?.email ?? "" },
  ];

  const contactFormTitle = isInEditor
    ? title
    : replaceStringPlaceholder(title ?? "", [{ placeholder: "Name", value: user?.first_name ?? "" }], false);

  const loyaltyFormContent =
    hasReachedParticipationLimit && !isInEditor ? (
      <div className={styles.alertBanner}>
        <AlertBanner
          tone="info"
          title={__("loyalty.contact_form.already_entered.title")}
          description={__("loyalty.contact_form.already_entered.description")}
        />
      </div>
    ) : (
      <LoyaltyContestFormProvider
        successScreenTitle={contactFormProps.successScreenTitle}
        title={contactFormTitle ?? ""}
        description={description}
        onSuccess={() => setIsSuccess(true)}
        sendButtonLabel={contactFormProps.sendButtonLabel}
        consent={consent}
        contestId={contestId}
        {...(!isInEditor &&
          contestsBackedConfig && {
            filePickerProps: getFilePickerPropsFromConfig(contestsBackedConfig),
            isFilePickerVisible: contestsBackedConfig?.file_upload_enabled,
          })}
      >
        {children}
      </LoyaltyContestFormProvider>
    );

  const columnClasses = classNames(styles.column, {
    [styles.columnSingle]: hasReachedParticipationLimit,
  });

  return (
    <div className={classNames(styles.wrapper, theme === "light" ? "light-theme" : "dark-theme")}>
      <div className={styles.headingInfo}>
        <Typography className={styles.heading} weight="light" variant="h2">
          {formHeading}
        </Typography>
        <Typography className={styles.subHeading} weight="light" variant="body1">
          {formSubHeading}
        </Typography>
      </div>
      <div className={styles.formContent}>
        <div className={columnClasses}>
          {isLoyaltyContestsServiceEnabled ? (
            loyaltyFormContent
          ) : (
            <ContactForm
              {...contactFormProps}
              onSuccess={() => setIsSuccess(true)}
              title={contactFormTitle ?? ""}
              description={description}
              className={styles.form}
              hiddenFields={hiddenFields}
            >
              <ConsentCheckbox consent={consent} displayConsent="always" showRequiredAsterisk={showRequiredAsterisk} />
            </ContactForm>
          )}
        </div>
        {!hasReachedParticipationLimit && (
          <div
            data-testid="loyalty-contact-form-img"
            className={classNames(styles.column, styles.image)}
            style={{ backgroundImage: `url(${imageSrc})` }}
          />
        )}
      </div>
    </div>
  );
};
